import type { ReviewSummary } from "../types";
import { Button } from "../ui";
import { useLabel } from "../lib/labels";
import { navigateExternal } from "../lib/postmessage";
import { RatingStars } from "./RatingStars";

interface Props {
  data: ReviewSummary;
  sessionToken: string;
}

export default function ReviewSummaryCard({ data, sessionToken }: Props) {
  // 기획: 별점(평점)이 아예 없으면 별점 카드 위젯 자체를 노출하지 않는다(리뷰 0건이면 평점도
  // 없어 함께 커버). 빈 히스토그램/0 Reviews 방지.
  if (!data.averageRating || !data.totalReviews) {
    return null;
  }
  const secondary = data.secondaryRatings ?? [];
  const hasOverall = typeof data.averageRating === "number" && data.averageRating > 0;
  const reviewsTpl = useLabel("reviews", "{totalReviews} Reviews");
  const viewAllLabel = useLabel("viewAllReviews", "View all Reviews");
  const countLabel = reviewsTpl.replace(
    "{totalReviews}",
    data.totalReviews.toLocaleString()
  );

  return (
    <div className="ac-review">
      {hasOverall && (
        <div className="ac-review__overall">
          <span className="ac-review__overall-num">
            {data.averageRating!.toFixed(1)}
          </span>
          <div className="ac-review__overall-meta">
            <RatingStars rating={data.averageRating!} className="ac-review__stars" size={14} />
            <span className="ac-review__overall-count">{countLabel}</span>
          </div>
        </div>
      )}

      {/* 기획: 유형별 평점(SecondaryRatings)이 있을 때만 하단 breakdown 노출.
          유형별이 없으면(예: 65MRGB87B9B DE) 히스토그램 폴백 없이 영역 자체를 숨긴다
          → 종합별점 + View all Reviews 만 남는다. */}
      {secondary.length > 0 && (
        <div className="ac-review__aspects">
          {secondary.map((s) => {
            // averageRating 를 5분할 세그먼트 기준으로 정규화한다.
            const range = s.valueRange || 5;
            const scaled = (s.averageRating / range) * 5;
            return (
              <div key={s.id} className="ac-review__aspect">
                <span className="ac-review__aspect-label">{s.label}</span>
                <div className="ac-review__aspect-row">
                  <div className="ac-review__segs" aria-hidden>
                    {Array.from({ length: 5 }, (_, i) => {
                      const fill = Math.max(0, Math.min(1, scaled - i));
                      return (
                        <div key={i} className="ac-review__seg">
                          <span
                            className="ac-review__seg-fill"
                            style={{ width: `${fill * 100}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <span className="ac-review__aspect-val">
                    {s.averageRating.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.reviewsSectionUrl && (
        <div className="ac-review__actions">
          <Button
            variant="secondary"
            size="xs"
            fullWidth
            onClick={() =>
              navigateExternal(data.reviewsSectionUrl!, undefined, {
                sessionId: sessionToken,
              })
            }
          >
            {viewAllLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
